<script setup lang="ts">
useHead({
  title: 'Terms of Service',
  meta: [
    { name: 'description', content: 'Terms of service for EclipseChase.is — usage rules, Pro tier terms, and governing law.' },
  ],
})
</script>

<template>
  <PageShell screen="terms" width="reading">
    <article class="legal-body">
      <h1 class="font-display text-3xl sm:text-4xl font-bold text-ink-1 mb-2">
        Terms of Service
      </h1>
      <Eyebrow tone="dim" class="mb-10">Last updated: March 19, 2026</Eyebrow>

      <div class="space-y-8 text-base text-ink-2 leading-relaxed">
        <section>
          <h2 class="font-display text-xl font-semibold text-ink-1 mb-3">2.1 Agreement to terms</h2>
          <p>
            By accessing or using EclipseChase.is, you agree to these terms. If you do not agree, do not use the service. You must be at least 18 years old or have parental/guardian consent.
          </p>
        </section>

        <section>
          <h2 class="font-display text-xl font-semibold text-ink-1 mb-3">2.2 Description of service</h2>
          <p>
            EclipseChase.is provides weather forecasts, location recommendations, and eclipse information to help users plan viewing of the August 12, 2026 total solar eclipse in Iceland. All information is for general guidance only and should not be relied upon as professional advice.
          </p>
        </section>

        <section>
          <h2 class="font-display text-xl font-semibold text-ink-1 mb-3">2.3 Pro tier</h2>
          <ul class="list-disc list-outside ml-5 space-y-2">
            <li><strong>One-time payment of €9.99</strong> via Stripe for access to premium features, including the live weather map, recommendation engine, and offline-ready map.</li>
            <li><strong>Digital content / digital service.</strong> By completing the purchase and requesting immediate access to Pro, you expressly consent to the immediate supply and performance of the digital content and digital service before the end of the statutory withdrawal period.</li>
            <li>You acknowledge that, once access to Pro has been granted, you <strong>lose your right of withdrawal</strong> under Article 16(m) of the EU Consumer Rights Directive, to the extent permitted by applicable consumer law.</li>
            <li><strong>Refunds:</strong> Once access to Pro has been granted, refunds are not available except where required by applicable law, including in cases of defective performance, lack of conformity, or other mandatory statutory consumer rights.</li>
            <li>If we are unable to provide access to Pro due to a technical issue on our side, we may provide a refund, restore access, or provide an equivalent remedy, as appropriate.</li>
            <li><strong>Pricing:</strong> Prices may change for future purchases, but changes will not affect purchases already completed.</li>
            <li><strong>License and access:</strong> Pro access is linked to the purchaser&rsquo;s email address, is personal and non-transferable, and is granted as a limited, non-exclusive, non-sublicensable right to use the Pro features for the period stated at the time of purchase.</li>
          </ul>
        </section>

        <section>
          <h2 class="font-display text-xl font-semibold text-ink-1 mb-3">2.4 Safety and liability disclaimer</h2>
          <p class="mb-3">
            EclipseChase.is provides informational guidance only, not professional meteorological, navigation, or safety advice.
          </p>
          <ul class="list-disc list-outside ml-5 space-y-2 mb-3">
            <li>Weather forecasts are sourced from vedur.is (Iceland Meteorological Office) and may be inaccurate, delayed, or incomplete</li>
            <li>Road condition data is sourced from Vegagerðin (Iceland Road Administration) and may not reflect real-time conditions</li>
            <li>Icelandic roads and weather conditions can be hazardous, including single-lane roads, river crossings, sudden weather changes, and limited cell coverage</li>
          </ul>
          <div class="my-4 px-3 py-2.5 ec-banner-warn text-xs font-mono">
            <strong>Important:</strong> Users are solely responsible for their own safety, including but not limited to:
            <ul class="list-disc list-outside ml-5 mt-2 space-y-1">
              <li>Driving decisions and route planning on Icelandic roads</li>
              <li>Checking official safety sources (safetravel.is, road.is) before traveling</li>
              <li>Proper eye protection when viewing the eclipse (never look directly at the sun without certified solar filters)</li>
              <li>Assessing local weather, terrain, and road conditions in person</li>
            </ul>
          </div>
          <p>
            We are not liable for any injury, loss, property damage, or other harm arising from reliance on information provided by the app.
          </p>
        </section>

        <section>
          <h2 class="font-display text-xl font-semibold text-ink-1 mb-3">2.5 Acceptable use</h2>
          <ul class="list-disc list-outside ml-5 space-y-2">
            <li>Do not scrape, crawl, or systematically extract data from the service</li>
            <li>Do not attempt to bypass Pro tier access controls</li>
            <li>Do not resell, redistribute, or share Pro access credentials</li>
            <li>Do not use the service for any unlawful purpose</li>
          </ul>
        </section>

        <section>
          <h2 class="font-display text-xl font-semibold text-ink-1 mb-3">2.6 Intellectual property</h2>
          <p>
            All content, design, code, eclipse computations, and data compilations on EclipseChase.is are the property of Elite Consulting, s.r.o. or its licensors. You may not reproduce, distribute, or create derivative works without written permission.
          </p>
        </section>

        <section>
          <h2 class="font-display text-xl font-semibold text-ink-1 mb-3">2.7 Service availability</h2>
          <ul class="list-disc list-outside ml-5 space-y-2">
            <li>The service is provided "as is" and "as available" without warranties of any kind, express or implied</li>
            <li>We do not guarantee uninterrupted or error-free operation</li>
            <li>We may modify, suspend, or discontinue the service at any time without notice</li>
            <li>Offline features depend on prior caching and may not include the latest data</li>
          </ul>
        </section>

        <section>
          <h2 class="font-display text-xl font-semibold text-ink-1 mb-3">2.8 Limitation of liability</h2>
          <p class="mb-3">To the maximum extent permitted by applicable law:</p>
          <ul class="list-disc list-outside ml-5 space-y-2">
            <li>Our total liability for any claims arising from your use of the service is limited to the amount you paid (€9.99 for Pro, or €0 for free tier)</li>
            <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages</li>
            <li>This includes, without limitation, loss of profits, data, or goodwill</li>
          </ul>
        </section>

        <section>
          <h2 class="font-display text-xl font-semibold text-ink-1 mb-3">2.9 Governing law and jurisdiction</h2>
          <p>
            These terms are governed by the laws of the Slovak Republic. Any disputes shall be resolved by the courts of Bratislava, Slovakia. This does not affect your mandatory consumer rights under the laws of your country of residence.
          </p>
        </section>

        <section>
          <h2 class="font-display text-xl font-semibold text-ink-1 mb-3">2.10 Changes to terms</h2>
          <p>
            We may update these terms. Material changes will be notified via email to Pro users and posted on the website. Continued use after changes constitutes acceptance.
          </p>
        </section>

        <section>
          <h2 class="font-display text-xl font-semibold text-ink-1 mb-3">2.11 Contact</h2>
          <p>
            For questions about these terms, contact us at <a href="mailto:privacy@eclipsechase.is" class="text-accent hover:text-accent-strong transition-colors">privacy@eclipsechase.is</a>.
          </p>
        </section>
      </div>
    </article>

    <AppFooter />
  </PageShell>
</template>

<style scoped>
.legal-body {
  padding: 32px 16px 16px;
}
@media (min-width: 768px) {
  .legal-body {
    padding: 64px 24px 32px;
  }
}
</style>

